import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Add from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import RestartAlt from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import WebsitePreview, { type PageSection } from "./WebsitePreview";
import apiService from "../../../service/Api";
import {
  AUDIENCES,
  AUDIENCE_META,
  AudienceSegment,
  FIELD_ICONS,
  VisibilityGroup,
  commonAudience,
  groupFields,
  hintFor,
  toneColor,
  unlockList,
  type Audience,
  type VisibilityField,
} from "../../venues/websiteVisibility";

const SETTINGS_URL = "/api/venues/website-settings/";

interface SettingsResponse {
  fields: VisibilityField[];
  groups: { key: string; default: string }[];
  sections: PageSection[];
  section_order: string[];
  updated_at: string | null;
  updated_by: string | null;
  can_edit: boolean;
}

type Draft = Record<string, Audience>;

function formatDate(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Each shown field's setting. Extra fields are in it only once added. */
const draftFrom = (fields: VisibilityField[]): Draft =>
  Object.fromEntries(fields.filter((field) => !field.optional || field.added).map((field) => [field.key, field.audience]));

/** Each group's current name. */
const namesFrom = (fields: VisibilityField[]): Record<string, string> =>
  Object.fromEntries(fields.map((field) => [field.group, field.group_label]));

export default function VenueWebsiteVisibility() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [order, setOrder] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(0);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  // The group a field was just added to, so it opens to show it.
  const [justAdded, setJustAdded] = useState<{ group: string; at: number } | null>(null);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    apiService
      .get<SettingsResponse>(SETTINGS_URL)
      .then(({ data }) => {
        if (active) {
          setSettings(data);
          setDraft(draftFrom(data.fields));
          setOrder(data.section_order ?? []);
          setNames(namesFrom(data.fields));
        }
      })
      .catch(() => active && setLoadError(true));
    return () => {
      active = false;
    };
  }, [retry]);

  // Fields under their groups' current (possibly unsaved) names.
  const allFields = useMemo(
    () => (settings?.fields ?? []).map((field) => ({ ...field, group_label: names[field.group] || field.group_label })),
    [settings, names],
  );
  const defaultNames = Object.fromEntries((settings?.groups ?? []).map((group) => [group.key, group.default]));
  const savedNames = useMemo(() => namesFrom(settings?.fields ?? []), [settings]);
  const renamed = Object.keys(savedNames).filter((group) => (names[group] || defaultNames[group]) !== savedNames[group]).length;
  // Shown: every core field, plus extra fields that have been added.
  const fields = allFields.filter((field) => !field.optional || field.key in draft);
  const available = allFields.filter((field) => field.optional && !(field.key in draft));
  const groups = groupFields(fields);
  const saved = useMemo(() => draftFrom(settings?.fields ?? []), [settings]);
  const savedOrder = settings?.section_order ?? [];
  const defaultOrder = (settings?.sections ?? []).map((section) => section.key);
  const orderChanged = order.join() !== savedOrder.join();
  // Rearranging the page counts as one change, and so does each renamed group.
  const changed = allFields.filter((field) => draft[field.key] !== saved[field.key]).length + (orderChanged ? 1 : 0) + renamed;
  const isDefault =
    order.join() === defaultOrder.join() &&
    Object.keys(defaultNames).every((group) => (names[group] || defaultNames[group]) === defaultNames[group]) &&
    allFields.every((field) => (field.optional ? !(field.key in draft) : draft[field.key] === field.default));
  const canEdit = Boolean(settings?.can_edit);
  const audienceOf = (field: VisibilityField) => draft[field.key] ?? field.audience;
  const rename = (group: string, name: string) => setNames((current) => ({ ...current, [group]: name || defaultNames[group] }));
  const set = (keys: string[], audience: Audience) =>
    setDraft((current) => ({ ...current, ...Object.fromEntries(keys.map((key) => [key, audience])) }));
  const addField = (field: VisibilityField) => {
    setAddAnchor(null);
    // Members by default: shown once saved, and easy to change.
    set([field.key], "members");
    setJustAdded({ group: field.group, at: Date.now() });
  };
  const removeField = (key: string) =>
    setDraft((current) => Object.fromEntries(Object.entries(current).filter(([item]) => item !== key)));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Core fields are stored only where they differ from the defaults, so later
      // defaults still reach untouched ones; added extra fields are always stored.
      const audiences = Object.fromEntries(
        fields
          .filter((field) => !field.locked && (field.optional || draft[field.key] !== field.default))
          .map((field) => [field.key, draft[field.key]]),
      );
      const { data } = await apiService.put<SettingsResponse>(SETTINGS_URL, { audiences, section_order: order, group_labels: names });
      setSettings(data);
      setDraft(draftFrom(data.fields));
      setOrder(data.section_order ?? []);
      setNames(namesFrom(data.fields));
      toast.success("Saved. Venue pages now follow these settings.");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError || !settings) {
    return (
      <PageShell>
        {loadError ? (
          <Alert severity="warning" action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
            Could not load the settings.
          </Alert>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography color="text.secondary">Loading…</Typography>
          </Stack>
        )}
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HEADER */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton aria-label="Back to configurations" onClick={() => navigate("/configurations")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={800}>Venue website visibility</Typography>
          <Typography color="text.secondary">Choose what visitors see on venue pages.</Typography>
        </Box>
        {canEdit && (
          <Tooltip title="Back to the defaults">
            <span>
              <Button
                startIcon={<RestartAlt />}
                color="inherit"
                disabled={isDefault || saving}
                onClick={() => {
                  setDraft(Object.fromEntries(allFields.filter((field) => !field.optional).map((field) => [field.key, field.default])));
                  setOrder(defaultOrder);
                  setNames(defaultNames);
                }}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Reset
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {/* LEGEND */}
      <Stack direction="row" sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }} alignItems="center">
        {AUDIENCES.map((audience) => (
          <Tooltip key={audience} title={AUDIENCE_META[audience].hint}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ px: 1.25, py: 0.6, borderRadius: 999, bgcolor: alpha(toneColor(theme, audience), 0.1), color: toneColor(theme, audience), "& svg": { fontSize: 16 } }}
            >
              {AUDIENCE_META[audience].icon}
              <Typography variant="body2" fontWeight={700}>{AUDIENCE_META[audience].label}</Typography>
              <Typography variant="body2" fontWeight={800} sx={{ opacity: 0.7 }}>
                {fields.filter((field) => audienceOf(field) === audience).length}
              </Typography>
            </Stack>
          </Tooltip>
        ))}
        <Box sx={{ flex: 1 }} />
        {settings.updated_by && (
          <Typography variant="caption" color="text.secondary">
            Updated by {settings.updated_by} · {formatDate(settings.updated_at)}
          </Typography>
        )}
      </Stack>

      {!canEdit && <Alert severity="info" sx={{ mt: 2 }}>Only Admins can change these.</Alert>}

      <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" }, gap: 4, alignItems: "start" }}>
        {/* GROUPS */}
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          {groups.map((group) => {
            const changeable = group.fields.filter((field) => !field.locked).map((field) => field.key);
            const opened = justAdded?.group === group.key;
            return (
              <VisibilityGroup
                // Re-mounted open when a field was just added here.
                key={opened ? `${group.key}-${justAdded?.at}` : group.key}
                defaultOpen={opened}
                groupKey={group.key}
                label={group.label}
                defaultLabel={defaultNames[group.key]}
                onRename={canEdit && !saving ? (name) => rename(group.key, name) : undefined}
                fields={group.fields}
                audienceOf={audienceOf}
                control={
                  <AudienceSegment
                    label={`all of ${group.label}`}
                    value={commonAudience(group.fields, audienceOf)}
                    disabled={!canEdit || saving}
                    onChange={(audience) => set(changeable, audience)}
                  />
                }
                renderField={(field) => ({
                  hint: hintFor(field.key, (key) => draft[key]),
                  onRemove: field.optional && canEdit && !saving ? () => removeField(field.key) : undefined,
                  control: (
                    <AudienceSegment
                      size="small"
                      label={field.label}
                      value={audienceOf(field)}
                      disabled={!canEdit || saving}
                      onChange={(audience) => set([field.key], audience)}
                    />
                  ),
                })}
              />
            );
          })}

          {canEdit && available.length > 0 && (
            <>
              <ButtonBase
                onClick={(event) => setAddAnchor(event.currentTarget)}
                aria-haspopup="menu"
                sx={{
                  justifyContent: "flex-start",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: 3,
                  border: "1.5px dashed",
                  borderColor: "divider",
                  color: "text.secondary",
                  transition: "all .15s",
                  "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "action.hover" }}>
                  <Add />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography fontWeight={800} color="inherit">Add field</Typography>
                  <Typography variant="caption" color="text.secondary">{available.length} more venue details you can publish</Typography>
                </Box>
              </ButtonBase>
              <Menu
                anchorEl={addAnchor}
                open={Boolean(addAnchor)}
                onClose={() => setAddAnchor(null)}
                slotProps={{ paper: { sx: { width: 300, maxHeight: 420, borderRadius: 3 } } }}
              >
                {groupFields(available).flatMap((group) => [
                  <ListSubheader key={`${group.key}-label`} sx={{ lineHeight: "32px", fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase" }}>
                    {group.label}
                  </ListSubheader>,
                  ...group.fields.map((field) => (
                    <MenuItem key={field.key} onClick={() => addField(field)} sx={{ gap: 1.5, py: 1 }}>
                      <Box sx={{ display: "flex", color: "text.secondary", "& svg": { fontSize: 19 } }}>{FIELD_ICONS[field.key]}</Box>
                      <Typography variant="body2" fontWeight={600}>{field.label}</Typography>
                    </MenuItem>
                  )),
                ])}
              </Menu>
            </>
          )}
        </Stack>

        {/* PREVIEW */}
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <WebsitePreview
            fields={fields}
            audienceOf={audienceOf}
            sections={settings.sections ?? []}
            order={order}
            onOrderChange={canEdit && !saving ? setOrder : undefined}
            unlocks={unlockList(fields, audienceOf)}
          />
        </Box>
      </Box>

      {/* SAVE BAR: only while there's something to save */}
      <Slide direction="up" in={canEdit && changed > 0} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%) !important",
            zIndex: (t) => t.zIndex.snackbar,
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 2.5,
            pr: 1,
            py: 1,
            borderRadius: 999,
            bgcolor: "grey.900",
            color: "#fff",
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mr: 1, whiteSpace: "nowrap" }}>
            {changed} {changed === 1 ? "change" : "changes"}
          </Typography>
          <Button size="small" disabled={saving} onClick={() => {
              setDraft(saved);
              setOrder(savedOrder);
              setNames(savedNames);
            }} sx={{ color: "grey.300", textTransform: "none", borderRadius: 999 }}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={saving}
            onClick={() => void save()}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, px: 2.25, boxShadow: "none" }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Paper>
      </Slide>
    </PageShell>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: "24px",
        p: { xs: 2, md: 4 },
        pb: { xs: 12, md: 12 },
        boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(216,221,235,0.2)"),
        minHeight: "100%",
      }}
    >
      {children}
    </Box>
  );
}
